import {
  FlexBox,
  Typography,
  Accordion,
  BasicCollapsible,
  Icon,
  Link,
  Button,
  useScreenClass,
} from "@vp/swan";
import React, { useContext } from "react";
import PageConfig from "../../utils/PageConfig";
import { MerchandisingProductView, MerchandisingDetail } from "../../types/ContentfulTypes";
import { DebugInfoSelectionsFields } from "./DebugInfoSelectionsFields";
import { DebugInfoTextField } from "./DebugInfoTextField";
import { updateMsxData } from "./ProductPageV2BffDatabaseClient";
import DebugInfoUtil from "./DebugInfoUtil";
import { getScreenClass } from "../../utils/screenClass";
import { PageStaticDataContext } from "../../contexts/PageStaticDataContextProvider";

export interface DebugMpvInfoProps {
  mpv: MerchandisingProductView;
  locale: string;
}

const MerchDetailDisplay = ({ merchDetail }: { merchDetail: MerchandisingDetail }) => {
  const { properties } = merchDetail;

  return (
    <FlexBox flexDirection="column">
      {(properties || []).map(property => (
        <FlexBox key={property.key} ml={"7"} mb={"5"} flexDirection="column">
          <FlexBox>
            <DebugInfoTextField label="Property key" value={property.key} />
            <DebugInfoTextField label="Property name" value={property.name} />
          </FlexBox>
          <FlexBox flexDirection="column" ml={"7"}>
            {(property.options || []).map(option => (
              <FlexBox key={option.key}>
                <DebugInfoTextField label="Option key" value={option.key} />
                <DebugInfoTextField label="Option name" value={option.name} />
              </FlexBox>
            ))}
            {(property.ranges || []).map(range => (
              <FlexBox key={range.key}>
                <DebugInfoTextField label="Minimum" value={range.minimum} />
                <DebugInfoTextField label="Maximum" value={range.maximum} />
                <DebugInfoTextField label="Increment" value={range.increment} />
              </FlexBox>
            ))}
          </FlexBox>
        </FlexBox>
      ))}
    </FlexBox>
  );
};

const DebugMpvInfo = () => {
  const [executionMessage, setExecutionMessage] = React.useState<string>("");
  const [showExecutionMessage, setShowExecutionMessage] = React.useState(true);
  const { isMobile } = getScreenClass(useScreenClass());
  const { locale, mpv } = useContext(PageStaticDataContext);

  const syncCurrentVersion = async () => {
    const tenant = PageConfig.tenant;
    const message = await updateMsxData({
      tenant,
      locale,
      productKey: mpv.coreProductId,
      version: mpv.version,
    });
    setExecutionMessage(message);
  };

  const toggleExecutionMessage = () => {
    setShowExecutionMessage(!showExecutionMessage);
  };

  const preselectedOptions = (mpv.defaultPreselectedOptions || []).reduce(
    (result, preselectedOptions) => {
      return {
        ...result,
        ...preselectedOptions.coreModelMappings,
      };
    },
    {}
  );
  const pimLink = DebugInfoUtil.generatePIMLink(mpv.coreProductId, `${mpv.version}`);

  return (
    <FlexBox flexDirection="column">
      <FlexBox justifyContent={"space-between"} flexDirection={isMobile ? "column" : "row"}>
        <FlexBox flexDirection="column">
          <Typography mb={"2"} mr={"4"} fontWeight="bold">
            MPV Information
          </Typography>
          <FlexBox flexDirection="column">
            <FlexBox flexDirection={isMobile ? "column" : "row"}>
              <DebugInfoTextField label="MPV ID" value={mpv.mpvId} />
              <FlexBox mr={"6"} alignItems="center">
                <DebugInfoTextField label="PRD" value={mpv.coreProductId} />
                <Link href={pimLink} target="_blank">
                  <Typography fontSize={"small"}>PIM</Typography>
                </Link>
              </FlexBox>
              <DebugInfoTextField label="Version" value={`${mpv.version}`} />
            </FlexBox>
            <FlexBox>
              <DebugInfoTextField label="Product name" value={mpv.name} />
            </FlexBox>
          </FlexBox>
        </FlexBox>
        <FlexBox flexDirection="column" style={{ flexBasis: "30%" }}>
          <Button onClick={syncCurrentVersion} size="mini" skin="primary">
            Sync current version
          </Button>
          <FlexBox flexDirection="column">
            {executionMessage && (
              <>
                <Typography fontSize={"xsmall"}>
                  Sync result:{" "}
                  <Link
                    render={p => (
                      <Button skin="link" className={p.className} onClick={toggleExecutionMessage}>
                        {p.children}
                      </Button>
                    )}
                  >
                    Toggle
                  </Link>
                </Typography>
                {showExecutionMessage && (
                  <Typography fontSize={"xsmall"}>
                    <pre>
                      <code style={{ whiteSpace: "break-spaces" }}>
                        {JSON.stringify(executionMessage, null, 2)}
                      </code>
                    </pre>
                  </Typography>
                )}
              </>
            )}
            {executionMessage && (
              <Typography fontSize={"xsmall"}>
                <Icon iconType="info" />
                The page will not be updated until the next build.
              </Typography>
            )}
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <Accordion pl={"7"}>
        <BasicCollapsible collapsibleId="collapsible-qty" heading={"Quantity"}>
          <FlexBox>
            <DebugInfoTextField label="Default qty" value={`${mpv.quantities?.default}`} />
            <DebugInfoTextField label="Recommended qty" value={`${mpv.quantities?.recommended}`} />
          </FlexBox>
        </BasicCollapsible>
        <BasicCollapsible
          collapsibleId="collapsible-preselected-option"
          heading="Preselected options"
        >
          <DebugInfoSelectionsFields selections={preselectedOptions} />
        </BasicCollapsible>
        <BasicCollapsible collapsibleId="collapsible-merch-detail" heading={"Merchandising Detail"}>
          {(mpv.merchandisingDetails || []).map((merchDetail, index) => {
            if (!["Product Page", "UploadFlow"].includes(merchDetail.type)) {
              return undefined;
            }
            return (
              <Accordion key={index} pl={"7"}>
                <BasicCollapsible
                  collapsibleId={`collapsible-merch-detail-${merchDetail.type}`}
                  heading={merchDetail.type}
                >
                  <MerchDetailDisplay merchDetail={merchDetail} />
                </BasicCollapsible>
              </Accordion>
            );
          })}
        </BasicCollapsible>
      </Accordion>
    </FlexBox>
  );
};

export default DebugMpvInfo;
